import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import DirectionsBikeIcon from '@material-ui/icons/DirectionsBike';
import TerrainIcon from '@material-ui/icons/Terrain';
import {
  Toolbar,
  SaveButton,
  DeleteButton,
  Create,
  DisabledInput,
  Edit,
  List,
  ReferenceField,
  ReferenceInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  EmailField,
  DateField,
  LongTextInput,
  required,
  UrlField,
  Filter,
  FunctionField,
} from 'react-admin';
import Datagrid from './detail/Datagrid';
import ShowButton from './button/ShowButton';
import DownloadButton from './button/DownloadButton';
import LongTextField from './fields/LongTextField';
import ConditionalField from './fields/ConditionalField';
import SelectInput from './inputs/SelectInput';
import DateInput from './inputs/DateInput';
import { generatePassword, getChoices } from '../utils.js';
import { withStyles } from '@material-ui/core';
import Query from './data/Query';
import { QUERY_PER_PAGE } from '../constants';

const TITLE_SINGULAR = 'Participant';
const TITLE_PLURAL = 'Participants';

const ACTIVITY_TYPE_CHOICES = [
  { id: 'bike', name: 'Bike', icon: DirectionsBikeIcon },
  { id: 'hike', name: 'Hike', icon: TerrainIcon },
];

const ActivityTypeOptionRenderer = choice => (
  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {choice.icon && React.createElement(choice.icon, { style: { fontSize: '1.2rem' } })}
    {choice.name}
  </span>
);

const ActivityTypeField = ({ record }) => {
  if (!record.tourId) return '-';

  return (
    <Query type="GET_ONE" resource="tour" payload={{ id: record.tourId }}>
      {({ data, loading }) => {
        if (loading) return '...';
        if (!data) return '-';
        const isBike = data.tourActivityType === 'bike';
        return (
          <span style={{ display: 'flex', alignItems: 'center' }} title={isBike ? 'Bike' : 'Hike'}>
            {isBike ? <DirectionsBikeIcon /> : <TerrainIcon />}
          </span>
        );
      }}
    </Query>
  );
};

const BikeField = ({ record }) => {
  if (!record.bikeId) return '-';

  return (
    <Query type="GET_ONE" resource="bike" payload={{ id: record.bikeId }}>
      {({ data, loading }) => {
        if (loading) return '...';
        if (!data) return '-';
        return (
          <Query type="GET_ONE" resource="bikeModel" payload={{ id: data.modelId }}>
            {({ data: model, loading: modelLoading }) => {
              if (modelLoading) return '...';
              if (!model) return `${data.code} - - - ${data.size}`;
              return `${data.code} - ${model.name} - ${data.size}`;
            }}
          </Query>
        );
      }}
    </Query>
  );
};

const TourNameField = ({ record }) => {
  if (!record.tourId) return '-';

  return (
    <Query type="GET_ONE" resource="tour" payload={{ id: record.tourId }}>
      {({ data, loading }) => {
        if (loading) return '...';
        if (!data) return '-';

        const truncatedName =
          data.name.length > 15 ? data.name.substring(0, 15) + '...' : data.name;

        return (
          <a
            href={`#/tour/${record.tourId}/show`}
            title={data.name}
            style={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: '#3f51b5',
            }}
          >
            {truncatedName}
          </a>
        );
      }}
    </Query>
  );
};

const toolbarStyles = {
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};

const ParticipantTitle = ({ record }) => (
  <span>
    {TITLE_SINGULAR} "{record.name}"
  </span>
);
ParticipantTitle.propTypes = { record: PropTypes.shape({ name: PropTypes.string }) };

const ParticipantFilters = props => (
  <Filter {...props}>
    <TextInput label="Search" source="q__name_email" alwaysOn />
    <SelectInput
      label="Activity"
      source="tour.tourActivityType"
      choices={ACTIVITY_TYPE_CHOICES}
      optionText={ActivityTypeOptionRenderer}
      alwaysOn
    />
  </Filter>
);

export const ParticipantList = props => (
  <List
    title={TITLE_PLURAL}
    {...props}
    sort={{ field: 'name', order: 'ASC' }}
    filters={<ParticipantFilters />}
  >
    <Datagrid rowClick="edit">
      <FunctionField label="Activity" render={record => <ActivityTypeField record={record} />} />
      <TextField source="name" />
      <EmailField source="email" />
      <TextField source="accessCode" />
      <FunctionField label="Bike" render={record => <BikeField record={record} />} />
      <TextField source="pedalSystem" label="Pedals" />
      <FunctionField label="Tour" render={record => <TourNameField record={record} />} />
      <ReferenceField source="languageId" reference="language" label="Language">
        <TextField source="code" />
      </ReferenceField>
      <ConditionalField showIf="routeBookUrl" label="Route Book">
        <DownloadButton source="routeBookUrl" />
      </ConditionalField>
      <ShowButton />
    </Datagrid>
  </List>
);

export const ParticipantShow = props => (
  <Show title={<ParticipantTitle />} {...props}>
    <SimpleShowLayout>
      <TextField source="name" />
      <EmailField source="email" />
      <TextField source="phoneNumber" />
      <ReferenceField source="tourId" reference="tour">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="accessCode" />
      <ReferenceField source="languageId" reference="language">
        <TextField source="code" />
      </ReferenceField>
      <UrlField source="routeBookUrl" />
      <TextField source="suggestedBikeModel" />
      <TextField source="suggestedBikeSize" />
      <ReferenceField source="bikeId" reference="bike" allowEmpty>
        <TextField source="code" />
      </ReferenceField>
      <TextField source="riderType" />
      <TextField source="healthConditions" />
      <TextField source="dietaryRestrictions" />
      <TextField source="jerseySize" />
      <TextField source="bibShortSize" />
      <TextField source="helmetSize" />
      <TextField source="pedalSystem" />
      <TextField source="height" />
      <TextField source="arrivalAirport" />
      <TextField source="arrivalFlightNumber" />
      <DateField source="arrivalDate" locales="pt-PT" />
      <TextField source="departureAirport" />
      <TextField source="departureFlightNumber" />
      <DateField source="departureDate" locales="pt-PT" />
      <TextField source="seatpostNoseToHandlebarCentre" />
      <TextField source="handlebarCentreToFrontAxle" />
      <TextField source="seatpostNoseToPedalAxle" />
      <TextField source="seatpostTopToPedalAxle" />
      <TextField source="roommateName" />
      <TextField source="preferredBedType" />
      <TextField source="withAdditionalMeals" />
      <LongTextField source="comments" />
    </SimpleShowLayout>
  </Show>
);

const ParticipantEditToolbar = withRouter(
  withStyles(toolbarStyles)(({ location, staticContext, ...restProps }) => {
    const locationState = location.state;
    const redirect = locationState && locationState.redirect;
    return (
      <Toolbar {...restProps}>
        <SaveButton redirect={redirect} undoable={false} />
        <DeleteButton redirect={redirect} undoable={false} />
      </Toolbar>
    );
  }),
);
ParticipantEditToolbar.displayName = 'ParticipantEditToolbar';

const TourOptionRenderer = choice => {
  const isBike = choice.tourActivityType === 'bike';
  const Icon = isBike ? DirectionsBikeIcon : TerrainIcon;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Icon style={{ fontSize: '1.2rem' }} />
      {choice.name}
    </span>
  );
};

const BikeInput = () => (
  <Query onlyData type="GET_ALL" resource="bikeModel" errorMsg="Failed to fetch bike models.">
    {bikeModels => (
      <Query onlyData type="GET_ALL" resource="bike" errorMsg="Failed to fetch bikes.">
        {bikes => {
          const choicesWithModel = getChoices(bikes, bikeModels, 'modelId', {
            resNameField: (bike, model) => `${bike.code} - ${model.name} - ${bike.size}`,
            sortBy: ['modelId', 'code'],
          });

          return <SelectInput label="Bike" source="bikeId" choices={choicesWithModel} />;
        }}
      </Query>
    )}
  </Query>
);

export const ParticipantEdit = props => (
  <Edit title={<ParticipantTitle />} {...props}>
    <SimpleForm toolbar={<ParticipantEditToolbar />}>
      <TextInput source="name" validate={[required()]} />
      <TextInput source="email" validate={[required()]} />
      <TextInput source="phoneNumber" />
      <ReferenceInput
        perPage={QUERY_PER_PAGE}
        source="tourId"
        reference="tour"
        validate={[required()]}
      >
        <SelectInput optionText={TourOptionRenderer} disabled />
      </ReferenceInput>
      <TextField source="accessCode" style={{ fontSize: '1rem' }} />
      <ReferenceInput
        perPage={QUERY_PER_PAGE}
        source="languageId"
        reference="language"
        validate={[required()]}
      >
        <SelectInput optionText="code" />
      </ReferenceInput>
      <UrlField source="routeBookUrl" label="Route book" />
      <BikeInput />
      <TextInput source="suggestedBikeModel" />
      <TextInput source="suggestedBikeSize" />
      <TextInput source="riderType" />
      <TextInput source="healthConditions" />
      <TextInput source="dietaryRestrictions" />
      <TextInput source="jerseySize" />
      <TextInput source="bibShortSize" />
      <TextInput source="helmetSize" />
      <TextInput source="pedalSystem" />
      <TextInput source="height" />
      <TextInput source="arrivalAirport" />
      <TextInput source="arrivalFlightNumber" />
      <DateInput source="arrivalDate" />
      <TextInput source="departureAirport" />
      <TextInput source="departureFlightNumber" />
      <DateInput source="departureDate" />
      <TextInput source="seatpostNoseToHandlebarCentre" />
      <TextInput source="handlebarCentreToFrontAxle" />
      <TextInput source="seatpostNoseToPedalAxle" />
      <TextInput source="seatpostTopToPedalAxle" />
      <TextInput source="roommateName" />
      <TextInput source="preferredBedType" />
      <TextInput source="withAdditionalMeals" />
      <LongTextInput source="comments" />
    </SimpleForm>
  </Edit>
);
ParticipantEdit.propTypes = { location: PropTypes.object.isRequired };

export const ParticipantCreate = props => {
  const locationState = props.location.state;
  return (
    <Create {...props}>
      <SimpleForm redirect={locationState && locationState.redirect}>
        <TextInput source="name" validate={[required()]} autoFocus />
        <TextInput source="email" validate={[required()]} />
        <TextInput source="phoneNumber" />
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="tourId"
          reference="tour"
          validate={[required()]}
        >
          <SelectInput optionText={TourOptionRenderer} />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="languageId"
          reference="language"
          validate={[required()]}
        >
          <SelectInput optionText="code" />
        </ReferenceInput>
        <DisabledInput source="routeBookUrl" />
        <DisabledInput
          source="accessCode"
          defaultValue={generatePassword}
          validate={[required()]}
        />
        <TextInput source="suggestedBikeModel" />
        <TextInput source="suggestedBikeSize" />
        <BikeInput />
        <TextInput source="riderType" />
        <TextInput source="healthConditions" />
        <TextInput source="dietaryRestrictions" />
        <TextInput source="jerseySize" />
        <TextInput source="bibShortSize" />
        <TextInput source="helmetSize" />
        <TextInput source="pedalSystem" />
        <TextInput source="height" />
        <TextInput source="arrivalAirport" />
        <TextInput source="arrivalFlightNumber" />
        <DateInput source="arrivalDate" />
        <TextInput source="departureAirport" />
        <TextInput source="departureFlightNumber" />
        <DateInput source="departureDate" />
        <TextInput source="seatpostNoseToHandlebarCentre" />
        <TextInput source="handlebarCentreToFrontAxle" />
        <TextInput source="seatpostNoseToPedalAxle" />
        <TextInput source="seatpostTopToPedalAxle" />
        <TextInput source="roommateName" />
        <TextInput source="preferredBedType" />
        <TextInput source="withAdditionalMeals" />
        <LongTextInput source="comments" />
      </SimpleForm>
    </Create>
  );
};
ParticipantCreate.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.object,
  }).isRequired,
};
